import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { API_BASE } from '../utils/api';
import './DogManagement.css';
import DogEditModal, { statusClassSlug } from './DogEditModal';

const photoSrc = (url) => (url && (url.startsWith('http') || url.startsWith('data:')) ? url : `${API_BASE || ''}${url || ''}`) || '';

function DogManagement({ currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isViewer = currentUser?.role === 'viewer';
  const canEdit = !isViewer;
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getTrackCountry = (trackName) => {
    if (!trackName) return null;
    
    const trackLower = trackName.toLowerCase().trim();
    
    // Irish tracks (ROI)
    const irishTracks = [
      'clonmel', 'curraheen park', 'drumbo park', 'dundalk', 'enniscorthy',
      'galway', 'kilkenny', 'lifford', 'limerick', 'mullingar', 'newbridge',
      'shelbourne park', 'thurles park', 'tralee', 'waterford', 'youghal'
    ];
    
    // UK tracks (GB)
    const ukTracks = [
      'central park', 'harlow', 'newcastle', 'swindon', 'nottingham', 'hove',
      'oxford', 'towcester', 'doncaster', 'kinsley', 'romford', 'sheffield',
      'yarmouth', 'dunstall park', 'monmore', 'star pelaw', 'suffolk downs',
      'sunderland', 'crayford', 'henlow', 'perry barr', 'valley',
      'belle vue', 'hall green', 'mildenhall', 'pelaw grange', 'peterborough',
      'poole', 'shawfield', 'sittingbourne', 'wimbledon'
    ];
    
    if (irishTracks.includes(trackLower)) return 'ROI';
    if (ukTracks.includes(trackLower)) return 'GB';
    
    // Try partial matches
    for (const irishTrack of irishTracks) {
      if (irishTrack.includes(trackLower) || trackLower.includes(irishTrack)) {
        return 'ROI';
      }
    }
    
    for (const ukTrack of ukTracks) {
      if (ukTrack.includes(trackLower) || trackLower.includes(ukTrack)) {
        return 'GB';
      }
    }
    
    return null;
  };

  const [selectedDogName, setSelectedDogName] = useState(null);

  // Display values (what user types) - update immediately
  const [searchTermDisplay, setSearchTermDisplay] = useState('');
  const [minAgeDisplay, setMinAgeDisplay] = useState('');
  const [maxAgeDisplay, setMaxAgeDisplay] = useState('');
  const [minRiskScoreDisplay, setMinRiskScoreDisplay] = useState('0');
  
  // Actual filter values (what triggers search) - updated via debounce
  const [searchTerm, setSearchTerm] = useState('');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [minRiskScore, setMinRiskScore] = useState('0');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDogs, setTotalDogs] = useState(0);
  const [showAdoptedOnly, setShowAdoptedOnly] = useState(false);
  const [skipNextFetch, setSkipNextFetch] = useState(false);
  const isManualFetchRef = useRef(false);
  const [trainerFilter, setTrainerFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [missingFilter, setMissingFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [excludeProcessed, setExcludeProcessed] = useState(false);
  const [hasIncidents, setHasIncidents] = useState(false);
  const [availableTrainers, setAvailableTrainers] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [allTrackNames, setAllTrackNames] = useState([]);
  const [enabledTracks, setEnabledTracks] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [unaccountedCount, setUnaccountedCount] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sexFilter, setSexFilter] = useState('');
  const [notSeen4Weeks, setNotSeen4Weeks] = useState(false);
  const [lastRaceIncidentFilter, setLastRaceIncidentFilter] = useState(false);
  const [exportingWall, setExportingWall] = useState(false);
  const perPage = 20;

  useEffect(() => {
    api.get('/api/unaccounted-count')
      .then(response => setUnaccountedCount(response.data.count))
      .catch(() => setUnaccountedCount(null));
  }, []);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (trainerFilter) params.trainer = trainerFilter;
      if (locationFilter) params.location = locationFilter;
      if (missingFilter) params.missing = missingFilter;
      if (minAge) params.min_age = minAge;
      if (maxAge) params.max_age = maxAge;
      if (minRiskScore && minRiskScore !== '0') params.min_risk_score = minRiskScore;
      if (excludeProcessed) params.exclude_processed = 'true';
      if (hasIncidents) params.has_incidents = 'true';
      if (lastRaceIncidentFilter) params.last_race_incident = 'true';
      params.sort_by = sortBy;
      params.sort_order = sortOrder;

      const response = await api.get('/api/dogs/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]
        : `dogs_export_${new Date().toISOString().slice(0,10)}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting dogs:', error);
      alert('Failed to export dogs. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportWallCSV = async () => {
    try {
      setExportingWall(true);
      const response = await api.get('/api/missing-dogs/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]
        : `missing_wall_${new Date().toISOString().slice(0,10)}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting missing wall:', error);
      alert('Failed to export missing wall. Please try again.');
    } finally {
      setExportingWall(false);
    }
  };

  useEffect(() => {
    api.get('/api/dog-filters/settings')
      .then(response => {
        const enabled = Array.isArray(response.data.enabledTracks) ? response.data.enabledTracks : [];
        setEnabledTracks(enabled);
      })
      .catch(() => setEnabledTracks([]));
  }, []);

  useEffect(() => {
    if (!showAdoptedOnly) {
      setFiltersLoading(true);
      const params = {};
      if (trainerFilter) params.trainer = trainerFilter;
      if (locationFilter) params.location = locationFilter;
      api.get('/api/dogs/filters', { params })
        .then(response => {
          setAvailableTrainers(response.data.trainers || []);
          const tracks = response.data.tracks || [];
          const names = tracks.map(t => typeof t === 'string' ? t : t.name);
          setAllTrackNames(names);
        })
        .catch(() => {})
        .finally(() => setFiltersLoading(false));
    } else {
      setFiltersLoading(false);
    }
  }, [showAdoptedOnly, trainerFilter, locationFilter]);

  useEffect(() => {
    if (enabledTracks.length > 0 && allTrackNames.length > 0) {
      setAvailableTracks(allTrackNames.filter(t => enabledTracks.includes(t)));
    } else {
      setAvailableTracks(allTrackNames);
    }
  }, [allTrackNames, enabledTracks]);

  // Debounce search term (800ms delay for text search)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchTermDisplay);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTermDisplay]);

  // Debounce number inputs (1000ms delay for age and risk score)
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinAge(minAgeDisplay);
    }, 1000);
    return () => clearTimeout(timer);
  }, [minAgeDisplay]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMaxAge(maxAgeDisplay);
    }, 1000);
    return () => clearTimeout(timer);
  }, [maxAgeDisplay]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinRiskScore(minRiskScoreDisplay);
    }, 1000);
    return () => clearTimeout(timer);
  }, [minRiskScoreDisplay]);

  const fetchDogs = useCallback(async (page = 1, adoptedFilter = showAdoptedOnly) => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = adoptedFilter ? '/api/adopted-dogs' : '/api/dogs';
      const requestId = Date.now();
      console.log(`[${requestId}] Fetching from:`, endpoint, 'adoptedFilter:', adoptedFilter, 'page:', page, 'search:', searchTerm);
      
      const params = { 
        page: page,
        per_page: perPage,
        search: searchTerm,
        include_count: 1
      };
      
      if (!adoptedFilter) {
        // Only add filters for regular dogs endpoint
        if (trainerFilter) params.trainer = trainerFilter;
        if (locationFilter) params.location = locationFilter;
        if (missingFilter) params.missing = missingFilter;
        if (statusFilter) params.status = statusFilter;
        if (minAge) params.min_age = minAge;
        if (maxAge) params.max_age = maxAge;
        if (minRiskScore && minRiskScore !== '0') params.min_risk_score = minRiskScore;
        if (excludeProcessed) params.exclude_processed = 'true';
        if (hasIncidents) params.has_incidents = 'true';
        if (sexFilter) params.sex = sexFilter;
        if (notSeen4Weeks) params.not_seen_4_weeks = 'true';
        if (lastRaceIncidentFilter) params.last_race_incident = 'true';
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
      }
      
      const response = await api.get(endpoint, { params });
      console.log(`[${requestId}] Response received from:`, endpoint, 'dogs count:', response.data.dogs?.length || 0, 'First dog:', response.data.dogs?.[0]?.name, 'Total:', response.data.total);
      
      // Only update state if this is still the current filter
      if (adoptedFilter === showAdoptedOnly) {
        setDogs(response.data.dogs || []);
        setCurrentPage(response.data.page || 1);
        setTotalPages(response.data.total_pages ?? 0);
        setTotalDogs(response.data.total ?? 0);
      } else {
        console.log(`[${requestId}] Ignoring response - filter changed from ${adoptedFilter} to ${showAdoptedOnly}`);
      }
    } catch (error) {
      console.error(`[${Date.now()}] Error fetching dogs:`, error);
      if (adoptedFilter === showAdoptedOnly) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to load dogs. Please try again later.';
        setError(`Error: ${errorMessage}`);
        setDogs([]);
      }
    } finally {
      if (adoptedFilter === showAdoptedOnly) {
        setLoading(false);
      }
    }
  }, [showAdoptedOnly, searchTerm, perPage, trainerFilter, locationFilter, missingFilter, statusFilter, minAge, maxAge, minRiskScore, sortBy, sortOrder, excludeProcessed, hasIncidents, sexFilter, notSeen4Weeks, lastRaceIncidentFilter]);

  useEffect(() => {
    // Reset to page 1 when search term or filter changes (but skip if we're manually fetching)
    if (!skipNextFetch) {
      setCurrentPage(1);
    }
  }, [searchTerm, showAdoptedOnly, trainerFilter, locationFilter, missingFilter, statusFilter, minAge, maxAge, minRiskScore, sortBy, sortOrder, excludeProcessed, hasIncidents, sexFilter, notSeen4Weeks, lastRaceIncidentFilter, skipNextFetch]);

  useEffect(() => {
    // Fetch dogs when page, filter, or search changes
    if (skipNextFetch || isManualFetchRef.current) {
      console.log('Skipping useEffect fetch - skipNextFetch:', skipNextFetch, 'isManualFetch:', isManualFetchRef.current);
      return;
    }
    console.log('useEffect triggered - fetching with showAdoptedOnly:', showAdoptedOnly, 'currentPage:', currentPage);
    fetchDogs(currentPage, showAdoptedOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, showAdoptedOnly, searchTerm, trainerFilter, locationFilter, missingFilter, statusFilter, minAge, maxAge, minRiskScore, sortBy, sortOrder, excludeProcessed, hasIncidents, sexFilter, notSeen4Weeks, lastRaceIncidentFilter]);

  // Check for dog parameter in URL and open modal
  useEffect(() => {
    const dogName = searchParams.get('dog');
    if (dogName && !selectedDogName) {
      // Decode the dog name and fetch details
      const decodedDogName = decodeURIComponent(dogName);
      const openDogDetails = async () => {
        try {
          await handleEdit({ name: decodedDogName });
          // Remove the parameter from URL after opening modal
          searchParams.delete('dog');
          setSearchParams(searchParams, { replace: true });
        } catch (error) {
          console.error('Error opening dog from URL:', error);
          searchParams.delete('dog');
          setSearchParams(searchParams, { replace: true });
        }
      };
      openDogDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const handleEdit = (dog) => {
    setSelectedDogName(dog.name);
  };

  const getRiskColor = (score) => {
    if (score >= 70) return '#dc2626';
    if (score >= 50) return '#ea580c';
    if (score >= 30) return '#d97706';
    return '#16a34a';
  };

  return (
    <div className="dog-management">
      <div className="management-header">
        <div>
          <h2>Dog Management</h2>
          <p>View dogs from the database and add notes/details to them</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleExportCSV} disabled={exporting} className="add-button" style={{ background: '#16a34a' }}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button onClick={handleExportWallCSV} disabled={exportingWall} className="add-button" style={{ background: '#2563eb' }}>
            {exportingWall ? 'Exporting...' : 'Export Missing Wall'}
          </button>
        </div>
      </div>
      {unaccountedCount !== null && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          padding: '10px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          <span style={{ fontSize: '18px' }}>!</span>
          <span>
            <strong>{unaccountedCount.toLocaleString()}</strong> unaccounted-for dogs
            <span style={{ color: '#92400e', marginLeft: '6px' }}>
              (active status, not missing)
            </span>
          </span>
        </div>
      )}

      {!showAdoptedOnly && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Trainer:</label>
            <select
              value={trainerFilter}
              onChange={(e) => setTrainerFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Trainers</option>
              {filtersLoading && availableTrainers.length === 0 ? <option disabled>Loading...</option> : null}
              {availableTrainers.map(trainer => (
                <option key={trainer} value={trainer}>{trainer}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Location:</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Locations</option>
              {filtersLoading && availableTracks.length === 0 ? <option disabled>Loading...</option> : null}
              {availableTracks.map(track => (
                <option key={track} value={track}>{track}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Missing:</label>
            <select
              value={missingFilter}
              onChange={(e) => setMissingFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Dogs</option>
              <option value="yes">Missing: YES</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '170px'
              }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="missing">Missing</option>
              <option value="retired">Retired</option>
              <option value="kept in kennels">Kept in Kennels</option>
              <option value="used for breeding">Used for Breeding</option>
              <option value="given up for adoption">Given Up for Adoption</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
              <option value="deceased">Deceased</option>
              <option value="killed">Killed</option>
              <option value="fallen">Fallen</option>
              <option value="ADOPTED">Adopted</option>
              <option value="On Rest / Season">On Rest / Season</option>
              <option value="Absent Due to Injury">Absent Due to Injury</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={excludeProcessed}
                onChange={(e) => setExcludeProcessed(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Exclude Processed
            </label>
            <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
              (Hide missing/adopted)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasIncidents}
                onChange={(e) => setHasIncidents(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Has Incidents
            </label>
            <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
              (Baulked/Disqualified/Stumbled/Fought)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Sex:</label>
            <select
              value={sexFilter}
              onChange={(e) => setSexFilter(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', minWidth: '100px' }}
            >
              <option value="">All</option>
              <option value="D">Dog</option>
              <option value="B">Bitch</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notSeen4Weeks}
                onChange={(e) => setNotSeen4Weeks(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Not Seen 4+ Weeks
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={lastRaceIncidentFilter}
                onChange={(e) => setLastRaceIncidentFilter(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Last Race Incident
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Age:</label>
            <input
              type="number"
              placeholder="Min"
              value={minAgeDisplay}
              onChange={(e) => setMinAgeDisplay(e.target.value)}
              min="0"
              max="20"
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                width: '70px'
              }}
            />
            <span style={{ fontSize: '14px' }}>to</span>
            <input
              type="number"
              placeholder="Max"
              value={maxAgeDisplay}
              onChange={(e) => setMaxAgeDisplay(e.target.value)}
              min="0"
              max="20"
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                width: '70px'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Min Risk Score:</label>
            <input
              type="number"
              placeholder="0"
              value={minRiskScoreDisplay}
              onChange={(e) => setMinRiskScoreDisplay(e.target.value)}
              min="0"
              max="100"
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                width: '80px'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="name">Name</option>
              <option value="risk_score">Risk Score</option>
              <option value="age">Age</option>
              <option value="last_race_date">Last Race Date</option>
              <option value="latest_track">Location</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        <div className="search-bar" style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="Search dogs by name..."
            value={searchTermDisplay}
            onChange={(e) => setSearchTermDisplay(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Immediately update search term when Enter is pressed
                setSearchTerm(searchTermDisplay);
                e.preventDefault();
              }
            }}
          />
        </div>
        <button
          onClick={async () => {
            const newValue = !showAdoptedOnly;
            console.log('Button clicked, toggling showAdoptedOnly from', showAdoptedOnly, 'to:', newValue);
            
            // Skip the next useEffect fetches since we're doing it manually
            isManualFetchRef.current = true;
            setSkipNextFetch(true);
            setLoading(true);
            setError(null);
            
            // Force immediate fetch with new value
            const endpoint = newValue ? '/api/adopted-dogs' : '/api/dogs';
            console.log('Immediately fetching from:', endpoint, 'with search:', searchTermDisplay);
            
            try {
              const response = await api.get(endpoint, {
                params: { 
                  page: 1,
                  per_page: perPage,
                  search: searchTermDisplay
                }
              });
              console.log('Direct fetch SUCCESS:', response.data.dogs?.length || 0, 'dogs from', endpoint);
              console.log('First dog name:', response.data.dogs?.[0]?.name);
              console.log('Total dogs:', response.data.total);
              
              // Update dogs data first
              setDogs(response.data.dogs || []);
              setCurrentPage(1);
              setTotalPages(response.data.total_pages ?? null);
              setTotalDogs(response.data.total ?? null);
              setLoading(false);
              
              // Update showAdoptedOnly AFTER a delay to prevent useEffect from running with old value
              // Keep skip flag true while we update the state
              setTimeout(() => {
                console.log('Updating showAdoptedOnly to:', newValue);
                setShowAdoptedOnly(newValue);
                // Keep skip flag true longer to prevent useEffect from running when showAdoptedOnly changes
                setTimeout(() => {
                  console.log('Resetting flags - useEffect can now run normally');
                  isManualFetchRef.current = false;
                  setSkipNextFetch(false);
                }, 500);
              }, 200);
            } catch (error) {
              console.error('Direct fetch error:', error);
              setError(`Error: ${error.message}`);
              setLoading(false);
              isManualFetchRef.current = false;
              setSkipNextFetch(false);
            }
          }}
          style={{
            padding: '8px 16px',
            backgroundColor: showAdoptedOnly ? '#16a34a' : '#f3f4f6',
            color: showAdoptedOnly ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: showAdoptedOnly ? '600' : '400',
            whiteSpace: 'nowrap'
          }}
        >
          {showAdoptedOnly ? '✓ Show All Dogs' : 'Show Adopted Dogs'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading dogs...</div>
      ) : error ? (
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      ) : (
      <div className="dogs-table-container">
        <table className="dogs-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Name</th>
              <th>Age</th>
              <th>Trainer</th>
              <th>Status</th>
              <th>Missing</th>
              <th>Risk Score</th>
              <th>Last Seen</th>
              <th>Location</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {dogs.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-state">
                  No dogs found.
                </td>
              </tr>
            ) : (
              dogs.map(dog => (
                <tr key={dog.name}>
                  <td>
                    {dog.photo_url ? (
                      <img 
                        src={photoSrc(dog.photo_url)} 
                        alt={dog.name}
                        className="dog-photo-thumbnail"
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    ) : (
                      <div className="photo-placeholder-small">
                        🖼️
                      </div>
                    )}
                  </td>
                  <td className="name-cell">
                    {dog.name}
                    {dog.sex && (
                      <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '6px' }}>
                        ({dog.sex.toUpperCase() === 'D' ? 'Male' : dog.sex.toUpperCase() === 'B' ? 'Female' : dog.sex})
                      </span>
                    )}
                    {dog.not_seen_4_weeks && (
                      <span style={{
                        display: 'inline-block', padding: '2px 6px', borderRadius: '4px',
                        fontSize: '11px', fontWeight: '600', marginLeft: '6px',
                        background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca'
                      }}>Not Seen 4+ Wks</span>
                    )}
                    {dog.last_race_incident && (
                      <span style={{
                        display: 'inline-block', padding: '2px 6px', borderRadius: '4px',
                        fontSize: '11px', fontWeight: '600', marginLeft: '6px',
                        background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa'
                      }}>Last Race Incident</span>
                    )}
                  </td>
                  <td>{dog.age || '-'}</td>
                  <td>{dog.trainer_name || '-'}</td>
                  <td>
                    <span className={`status-badge ${statusClassSlug(dog.status)}`}>
                      {dog.status || 'active'}
                    </span>
                    {dog.adoption && dog.adoption.has_adoption_group && (
                      <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px' }}>
                        ✓ Adopted by {dog.adoption.adoption_group || 'Unknown Group'}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`missing-badge ${dog.is_missing ? 'missing' : 'found'}`}>
                      {dog.is_missing ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    {dog.adoption && dog.adoption.has_adoption_group ? (
                      <span className="risk-badge" style={{ backgroundColor: '#16a34a' }}>
                        0
                      </span>
                    ) : (
                      <span 
                        className="risk-badge" 
                        style={{ backgroundColor: getRiskColor(dog.risk_score || 0) }}
                      >
                        {dog.risk_score !== undefined ? dog.risk_score : '-'}
                      </span>
                    )}
                  </td>
                  <td>{dog.last_race_date ? new Date(dog.last_race_date).toLocaleDateString('en-GB') : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{dog.latest_track || '-'}</span>
                      {dog.latest_track && (() => {
                        const country = getTrackCountry(dog.latest_track);
                        if (country === 'GB') {
                          return (
                            <img 
                              src="/gb.webp" 
                              alt="United Kingdom" 
                              title="United Kingdom"
                              style={{ width: '20px', height: '10px', objectFit: 'contain' }}
                            />
                          );
                        } else if (country === 'ROI') {
                          return (
                            <img 
                              src="/roi.webp" 
                              alt="Republic of Ireland" 
                              title="Republic of Ireland"
                              style={{ width: '20px', height: '10px', objectFit: 'contain' }}
                            />
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </td>
                  <td>
                    {canEdit && (
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(dog)}
                          className="edit-button"
                          title="Edit"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                    {!canEdit && <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>View Only</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        
        {dogs.length > 0 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {currentPage}{totalPages > 0 ? ` of ${totalPages}` : ''}{totalDogs > 0 ? ` (${totalDogs} dogs)` : (dogs.length >= perPage ? ' (more available)' : '')}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={totalPages > 0 ? currentPage >= totalPages : (dogs.length < perPage)}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
      )}

      {selectedDogName && (
        <DogEditModal
          dogName={selectedDogName}
          currentUser={currentUser}
          onClose={() => setSelectedDogName(null)}
          onSaved={() => fetchDogs(currentPage)}
        />
      )}
    </div>
  );
}

export default DogManagement;

